/**
 * KAN-828: галочка "Старший менеджер" в карточке менеджера. Старший видит
 * всех клиентов и переназначает любых. Ставят владелец и оператор
 * (canSetSeniorManager), сохраняется сразу: PATCH /managers/{id}/ {is_senior};
 * после сохранения обновляются менеджеры (useManagerMutations).
 */
import { useId } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useManagerMutations } from '@/hooks/useManagers';
import { extractErrorMessage } from '@/lib/extractErrorMessage';
import { canSetSeniorManager } from '@/lib/managerAccess';

export const SENIOR_MANAGER_LABEL = 'Старший менеджер';
export const SENIOR_MANAGER_HINT = 'Видит всех клиентов и может переназначать любые заявки';

interface Props {
  managerId: string;
  isSenior: boolean;
}

export function ManagerSeniorToggle({ managerId, isSenior }: Props): JSX.Element | null {
  const { data: user } = useCurrentUser();
  const { update } = useManagerMutations();
  const id = useId();
  if (!canSetSeniorManager(user)) return null;

  const checked = update.isPending ? !!update.variables?.data.is_senior : isSenior;
  return (
    <div className="flex items-start gap-2" data-testid="manager-senior">
      <Checkbox
        id={id}
        checked={checked}
        disabled={update.isPending}
        aria-describedby={`${id}-hint`}
        onCheckedChange={(value) => update.mutate({ id: managerId, data: { is_senior: value } })}
      />
      <div className="text-sm">
        <label htmlFor={id} className="font-medium text-gray-900">
          {SENIOR_MANAGER_LABEL}
        </label>
        <p id={`${id}-hint`} className="text-xs text-gray-500">
          {SENIOR_MANAGER_HINT}
        </p>
        {update.error && (
          <p className="mt-1 text-xs text-red-700" data-testid="manager-senior-error">
            {extractErrorMessage(update.error, 'Не удалось сохранить')}
          </p>
        )}
      </div>
    </div>
  );
}
